/**
 * user-profile.component.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { Component, OnInit } from "@angular/core";
import { Modal } from "angular2-modal/plugins/bootstrap";

import { AlertService } from "../../common/alert.service";
import { ModalHelpers, oneline } from "../../support/utilities";
import { ChangePasswordModalComponent }
  from "../change-password-modal.component";
import { ChangeSecurePromptsModalComponent }
  from "../change-secure-prompts-modal.component";

import { Rule, RULE_NAMES } from "../permissions/rules";
import { UserService } from "./user.service";
import { ModalService } from "app/common/modal.service";
import { AcknowledgmentResponse } from "app/server/messages/general";

/**
 * The {@link UserProfileComponent} presents a detailed summary of the current
 * user, including identity, organization membership, and permissions. It also
 * presents some actions for modifying user credentials and accessing certain
 * role-specific behaviors.
 */
@Component({
    selector: "user-profile",
    template: `
<h2>User Profile</h2>
<table class="table table-striped center-items" *ngIf="users.self">
    <tbody>
        <tr>
            <th>Name</th>
            <td>
                <p>
                    {{users.self.name}}
                    <span
                        *ngIf="users.self.dev"
                        class="glyphicon glyphicon-sunglasses">
                    </span>
                </p>
            </td>
            </tr>
        <tr>
            <th>Organization</th>
            <td><p>{{users.self.org}}</p></td>
        </tr>
        <tr>
            <th>Groups</th>
            <td>
                <div *ngFor="let group of users.self.groups">
                    {{group.name}}
                    <div *ngIf="users.debugMode" class="debug-only">
                        <span class="badge">id #{{group.id}}</span>
                        <strong
                            *ngIf="group.isPrimary"
                            class="">
                            primary group
                        </strong>
                        <span
                            *ngIf="!group.isPrimary && group.primaryGroup"
                            class="">
                            primary = {{group.primaryGroup.name}}
                            ({{group.primaryGroupId}})
                        </span>
                        <p>
                            <em>rules:</em>
                            [<span *ngFor="let rule of group.rules; let l = last">
                                {{ruleName(rule)}}{{l ? "" : ","}}
                            </span>]
                        </p>
                    </div>
                </div>
            </td>
        </tr>
        <tr *ngIf="users.otherSessions.length > 0">
            <th>Sessions</th>
            <td>
              <p *ngFor="let session of users.otherSessions; let last = last">
                  <em>last used</em>
                  {{session.connectedSince | calendarTime}}
                  <br>
                  <em>from</em>
                  {{users.getIPCommonName(session.client)}}
                  {{session.client}}
                  <br>
                  <em>via zone</em>
                  <a [href]="session.zone">{{session.zoneTitle}}</a>
              </p>
              <button
                class="btn btn-danger"
                (click)="killSessions()">
                End Other Sessions
              </button>
            </td>
        </tr>
        <tr *ngIf="users.debugMode" class="debug-only">
            <th>System Rules</th>
            <td>
                <ul>
                    <li *ngFor="let rule of users.self.rules">
                        {{ruleName(rule)}}
                    </li>
                </ul>
            </td>
        </tr>
        <tr *ngIf="users.debugMode" class="debug-only">
            <th>Zones</th>
            <td>
                <p *ngFor="let zone of users.self.zones">
                    <label>
                        {{zone.title}}
                        <span class="badge">
                            #{{zone.zoneId}}
                        </span>
                    </label>
                    <br>
                    {{zone.organizationName}}
                    <br>
                    <a [href]="zone.url">
                        {{zone.url}}
                    </a>
                </p>
            </td>
        </tr>
        <tr>
            <th>Account</th>
            <td>
                <button
                    type="button"
                    class="btn btn-default form-control"
                    (click)="openPasswordModal()">
                    Change Password
                </button>
                <button
                    type="button"
                    class="btn btn-default form-control"
                    (click)="openPromptsModal()">
                    Change Secure Prompts
                </button>
            </td>
        </tr>
        <tr *ngIf="users.self.algorithmDev"> <!-- TODO: other cases here -->
            <th>Role-Specific Actions</th>
            <td>
                <button
                    *ngIf="users.self.algorithmDev"
                    type="button"
                    class="btn btn-default form-control"
                    (click)="openHammurabiDocs()">
                    View Hammurabi Docs
                </button>
            </td>
        </tr>
        <tr *ngIf="users.debugMode && users.debugActions">
            <th>Dev-only Actions</th>
            <td class="text-center">
                <kbd>(None available)</kbd>
            </td>
        </tr>
    </tbody>
</table>
`
})
export class UserProfileComponent
{
    constructor (
        public users : UserService,
        private DEP_modal : Modal,
        private modals : ModalService,
        private alerts : AlertService
    ) {}

    /**
     * Present a modal dialog for changing the user's password.
     * (Note that all messaging is handled by the dialog class itself.)
     */
    public openPasswordModal () : void
    {
        this.modals.observe(
          ChangePasswordModalComponent,
          {size: "sm"}
        ).subscribe((saved : boolean) =>
            saved
              ? this.alerts.success(`Your password has been changed!`)
              : null
        );
    }

    /**
     * Present a modal dialog for changing the user's secure prompts.
     * (Note that all messaging is handled by the dialog class itself.)
     */
    public openPromptsModal () : void
    {
        ModalHelpers.observeCustom(
            this.DEP_modal,
            ChangeSecurePromptsModalComponent,
            {size: "lg"}
        ).subscribe((saved : boolean) =>
            saved
                ? this.alerts.success(`Your secure prompts have been changed!`)
                : null
        );
    }

    /**
     * Open a new window to the Hammurabi (DSL) documentation. This option
     * should only be available to subject matter experts, as identified by
     * the `DEVELOP_ALGORITHM` {@link Rule}.
     */
    public openHammurabiDocs () : void
    {
        const HAMMURABI_DOCS_URL : string =
            `static/docs/Core/Hammurabi/index.html`;
        window.open(HAMMURABI_DOCS_URL);
    }

    /**
     * Transform a {@link Rule} enum into a human-readable label, if one is
     * available.
     * @param rule The rule to stringify
     */
    public ruleName (rule : Rule) : string
    {
        return RULE_NAMES.get(rule) || `${Rule[rule]} (${rule})`;
    }

    public killSessions () : void
    {
      const count : number = this.users.otherSessions.length;
      this.modals.confirm({
        title: "Kill active sessions",
        body: oneline`Are you sure you want to force your other active
          sessions to disconnect?`
      }).flatMap(() => this.users.killAllSessions(false))
        .subscribe((m : AcknowledgmentResponse) => {
          if (m.ok)
          {
            this.modals.alert(oneline`
              Successfully ended ${count} session(s). You will need to
              reload and log in again on the affected device(s).`);
          }
        });
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/auth/identity/user-profile.component.ts